/**
 * This file contains utility functions for handling different types of asset nodes
 * (lines, sections, towers, spans) in the EnerMetis Power Grid system.
 * It includes functions for processing location data and finding nodes within
 * the project structure.
 */

import { debugLog } from '../constants/debug';
import { AssetNode, ProjectData } from '../types/project';
import { ColumnDefinition } from '../types/table';

/**
 * Interface representing the processed location data for any asset node
 * Contains various properties that describe the node's location and characteristics
 */
export interface ProcessedNodeData {
    [key: string]: any;
    structureNameAtLocation?: string;
    name?: string;
    type?: 'line' | 'section' | 'tower' | 'span';
    properties?: Record<string, any>;
}

/**
 * Processes raw node data according to the provided column definitions
 * @param data - Raw node data containing properties
 * @param columns - Column definitions that specify how to process each field
 * @returns Processed data with formatted values according to column specifications
 */
export function processNodeData(
    data: ProcessedNodeData, 
    columns: ColumnDefinition[]
): Record<string, any> {
    // Initialize an empty object to store processed data
    const processed = columns.reduce<Record<string, any>>((acc, column) => {
        // Try to find the property in either the main data object or its properties
        let value = data[column.key];
        
      /* This part of the `processNodeData` function is checking if a specific property is not found in
      the main data object, it then checks if the property exists within the `properties` object of
      the node data. */
        // If not found in main data, check properties
        if (value === undefined && data.properties) {
            const prop = data.properties[column.key];
            // Handle both direct values and dynamic property values
            value = typeof prop === 'object' && prop !== null && 'value' in prop
                ? prop.value
                : prop;
        }
        
        // If still not found, set to null
        value = value ?? null;
        
        // Format numeric values to 2 decimal places for better readability
        if (typeof value === 'number') {
            value = Number(value.toFixed(2));
        }
                     
        acc[column.key] = value;
        return acc;
    }, {});

    // Log the processing results for debugging
    debugLog('Processed node data:', { input: data, output: processed });
    return processed;
}

/**
 * Searches for a specific node across all sections in the project data
 * @param projectData - The complete project data structure
 * @param nodeName - Name of the node to find
 * @param nodeType - Type of node to search for ('line' | 'section' | 'tower' | 'span')
 * @returns Array of processed location data for matching nodes
 */
export function findNodesByName(
    projectData: ProjectData, 
    nodeName: string,
    nodeType: 'line' | 'section' | 'tower' | 'span'
): ProcessedNodeData[] {
    debugLog('findNodesByName called with:', { nodeName, nodeType });
    const results: ProcessedNodeData[] = [];
    
    function searchNode(node: AssetNode): boolean {
        debugLog('Checking node:', { 
            nodeName: node.name, 
            nodeType: node.type,
            searchingFor: { name: nodeName, type: nodeType }
        });
        
        // Check if current node matches criteria
        if (node.type === nodeType && node.name === nodeName) {
            debugLog('Found matching node:', node);
            results.push({
                structureNameAtLocation: node.name,
                name: node.name,
                type: node.type,
                properties: node.properties
            });
            return true; // Found the node, stop searching
        }
        
        // Only search children if we haven't found the node yet
        if (node.children) {
            for (const child of node.children) {
                if (searchNode(child)) {
                    return true; // Propagate the "found" signal up
                }
            }
        }
        
        return false; // Node not found in this branch
    }
    
    // Start search from the root
    if (projectData.Lines) {
        searchNode(projectData.Lines);
    }
    
    debugLog('findNodesByName results:', { 
        searchedFor: { name: nodeName, type: nodeType },
        resultsCount: results.length,
        results 
    });
    return results;
}

/**
 * Gets all nodes of a specific type within a parent node
 * @param projectData - The complete project data structure
 * @param parentName - Name of the parent node to search in
 * @param parentType - Type of the parent node ('line' | 'section')
 * @param childType - Type of children to find ('section' | 'tower' | 'span')
 * @returns Array of processed location data for all matching nodes
 */
export function getNodesByParent(
    projectData: ProjectData,
    parentName: string,
    parentType: 'line' | 'section',
    childType: 'section' | 'tower' | 'span'
): ProcessedNodeData[] {
    const results: ProcessedNodeData[] = [];
    
    function findParentAndCollectChildren(node: AssetNode) {
        // Check if this is the parent we're looking for
        if (node.type === parentType && 
            node.name.toLowerCase() === parentName.toLowerCase()) {
            // Collect all children of the specified type
            node.children?.forEach(child => {
                if (child.type === childType) {
                    results.push({
                        structureNameAtLocation: child.name,
                        name: node.name,
                        type: child.type,
                        properties: child.properties
                    });
                }
            });
            return; // Found the parent, no need to search further
        }
        
        // Continue searching in children
        node.children?.forEach(child => findParentAndCollectChildren(child));
    }
    
    if (projectData.Lines) {
        findParentAndCollectChildren(projectData.Lines);
    }
    
    return results;
}

/**
 * Gets all nodes of a specific type from the entire project
 * @param projectData - The complete project data structure
 * @param nodeType - Type of nodes to collect ('line' | 'section' | 'tower' | 'span')
 * @returns Array of processed location data for all matching nodes
 */
export function getAllNodesOfType(
    projectData: ProjectData,
    nodeType: 'line' | 'section' | 'tower' | 'span'
): ProcessedNodeData[] {
    const results: ProcessedNodeData[] = [];
    
    function collectNodes(node: AssetNode) {
        // If this node is of the type we're looking for, add it
        if (node.type === nodeType) {
            results.push({
                structureNameAtLocation: node.name,
                name: node.name,
                type: node.type,
                properties: node.properties
            });
        }
        
        // Continue collecting from children
        node.children?.forEach(child => collectNodes(child));
    }
    
    if (projectData.Lines) {
        collectNodes(projectData.Lines);
    }
    
    return results;
}
